import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, unref as _unref } from "vue";
const _hoisted_1 = { class: "queued-actions-page" };
const _hoisted_2 = { class: "merchant-cell" };
const _hoisted_3 = ["src", "alt"];
const _hoisted_4 = ["src", "alt"];
const _hoisted_5 = { class: "merchant-cell" };
const _hoisted_6 = ["src", "alt"];
const _hoisted_7 = {
    key: 0,
    class: "rate-locked"
};
import { ref, computed, onMounted } from 'vue';
import AdminTable from '@/admin/components/AdminTable.vue';
import { useAdminDiscountActions } from '@/composables/useAdminDiscountActions';
import { useAdmin } from '@/composables/useAdmin';
const W = 'minmax(0, 1fr)';
export default /*@__PURE__*/ _defineComponent({
    __name: 'QueuedDiscountActionsPage',
    setup(__props) {
        const { getQueuedRateActions, addQueuedRateAction, deleteQueuedRateAction } = useAdminDiscountActions();
        const admin = useAdmin();
        const tableRef = ref(null);
        const loading = ref(true);
        const adding = ref(false);
        const deletingId = ref(null);
        const rows = ref([]);
        const discounts = ref([]);
        const showErrorToast = ref(false);
        const errorMessage = ref('');
        function showError(message) {
            errorMessage.value = message;
            showErrorToast.value = true;
        }
        // width: 'minmax(0, 1fr)' forces every row's grid to identical equal columns (plain '1fr' = minmax(auto,1fr)
        // lets the add-row's form controls widen their columns, knocking them out of line with the text data rows).
        // discountId/scheduleDateTime required; newDiscountRate validated in handleAdd (only when not reset).
        const columns = [
            { field: 'discountId', label: 'Discount', required: true, width: W },
            { field: 'action', label: 'Action', width: W, default: 'rate' },
            { field: 'newDiscountRate', label: 'New Rate (%)', width: W },
            { field: 'scheduleDateTime', label: 'Schedule', required: true, width: W },
        ];
        // Action types are mutually exclusive — only 'rate' uses newDiscountRate; the rest send just discountId + schedule.
        const actionOptions = [
            { value: 'rate', label: 'Set New Rate' },
            { value: 'reset', label: 'Reset to Tillo Rate' },
            { value: 'activate', label: 'Activate' },
            { value: 'deactivate', label: 'Deactivate' },
        ];
        const isRateAction = (row) => !row.isReset && !row.isActivate && !row.isDeactivate;
        const actionLabel = (row) => row.isReset ? 'Reset to Tillo Rate'
            : row.isActivate ? 'Activate'
                : row.isDeactivate ? 'Deactivate'
                    : 'Set New Rate';
        // Local now as YYYY-MM-DDTHH:mm for the datetime-local min (toISOString is UTC, so offset-correct first)
        const nowLocal = (() => {
            const d = new Date();
            d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
            return d.toISOString().slice(0, 16);
        })();
        const discountOptions = computed(() => discounts.value.map(d => ({ id: d.id, name: d.merchant?.name ?? d.id, logoUrl: d.merchant?.logoUrl ?? '' })));
        const findDiscount = (id) => discounts.value.find(d => d.id === id);
        const discountName = (id) => findDiscount(id)?.merchant?.name ?? id;
        const discountLogo = (id) => findDiscount(id)?.merchant?.logoUrl ?? '';
        function handleImageError(event) {
            event.target.style.display = 'none';
        }
        function formatDateTime(value) {
            if (!value)
                return '—';
            const date = new Date(value);
            return isNaN(date.getTime()) ? value : date.toLocaleString('en-GB', { timeZone: 'UTC' }) + ' UTC';
        }
        function load() {
            getQueuedRateActions((result) => {
                rows.value = result ?? [];
                loading.value = false;
            });
        }
        function handleAdd(data) {
            const actionType = data.action || 'rate';
            if (actionType === 'rate' && !data.newDiscountRate) {
                showError('Enter a new rate, or choose a different action.');
                return;
            }
            if (new Date(data.scheduleDateTime) < new Date()) {
                showError('Cannot schedule an action in the past.');
                return;
            }
            adding.value = true;
            const action = {
                discountId: data.discountId,
                isReset: actionType === 'reset',
                isActivate: actionType === 'activate',
                isDeactivate: actionType === 'deactivate',
                newDiscountRate: actionType === 'rate' ? Number(data.newDiscountRate) : 0,
                scheduleDateTime: new Date(data.scheduleDateTime).toISOString(),
            };
            addQueuedRateAction(action, () => { adding.value = false; tableRef.value?.resetAddRow(); load(); }, (msg) => { adding.value = false; showError(msg); });
        }
        function handleDelete(row) {
            deletingId.value = row.id;
            deleteQueuedRateAction(row.id, () => {
                deletingId.value = null;
                load();
            });
        }
        onMounted(() => {
            admin.getVouchers((result) => {
                discounts.value = (result ?? []).filter(d => d.merchant);
            });
            load();
        });
        return (_ctx, _cache) => {
            const _component_v_list_item = _resolveComponent("v-list-item");
            const _component_v_select = _resolveComponent("v-select");
            const _component_v_text_field = _resolveComponent("v-text-field");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_snackbar = _resolveComponent("v-snackbar");
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createVNode(AdminTable, {
                    ref_key: "tableRef",
                    ref: tableRef,
                    title: "Queued Discount Rate Actions",
                    icon: "⏰",
                    columns: columns,
                    data: rows.value,
                    loading: loading.value,
                    addable: true,
                    deletable: true,
                    "adding-row": adding.value,
                    "deleting-row-id": deletingId.value,
                    "empty-message": "",
                    onRowAdd: handleAdd,
                    onRowDelete: handleDelete
                }, {
                    "cell-discountId": _withCtx(({ row }) => [
                        _createElementVNode("div", _hoisted_2, [
                            (discountLogo(row.discountId))
                                ? (_openBlock(), _createElementBlock("img", {
                                    key: 0,
                                    src: discountLogo(row.discountId),
                                    alt: discountName(row.discountId),
                                    class: "merchant-logo",
                                    onError: handleImageError
                                }, null, 40, _hoisted_3))
                                : _createCommentVNode("", true),
                            _createElementVNode("span", null, _toDisplayString(discountName(row.discountId)), 1)
                        ])
                    ]),
                    "cell-action": _withCtx(({ row }) => [
                        _createTextVNode(_toDisplayString(actionLabel(row)), 1)
                    ]),
                    "cell-newDiscountRate": _withCtx(({ row }) => [
                        _createTextVNode(_toDisplayString(isRateAction(row) ? row.newDiscountRate + '%' : '—'), 1)
                    ]),
                    "cell-scheduleDateTime": _withCtx(({ row }) => [
                        _createTextVNode(_toDisplayString(formatDateTime(row.scheduleDateTime)), 1)
                    ]),
                    "add-cell-discountId": _withCtx(({ addRowData }) => [
                        _createVNode(_component_v_select, {
                            modelValue: addRowData.discountId,
                            "onUpdate:modelValue": ($event) => ((addRowData.discountId) = $event),
                            items: discountOptions.value,
                            "item-title": "name",
                            "item-value": "id",
                            density: "compact",
                            variant: "outlined",
                            "hide-details": "",
                            placeholder: "Select merchant…"
                        }, {
                            item: _withCtx(({ props: itemProps, item }) => [
                                _createVNode(_component_v_list_item, _normalizeProps(_guardReactiveProps(itemProps)), {
                                    prepend: _withCtx(() => [
                                        (item.raw.logoUrl)
                                            ? (_openBlock(), _createElementBlock("img", {
                                                key: 0,
                                                src: item.raw.logoUrl,
                                                alt: item.raw.name,
                                                class: "merchant-logo",
                                                onError: handleImageError
                                            }, null, 40, _hoisted_4))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _: 2
                                }, 1040)
                            ]),
                            selection: _withCtx(({ item }) => [
                                _createElementVNode("div", _hoisted_5, [
                                    (item.raw.logoUrl)
                                        ? (_openBlock(), _createElementBlock("img", {
                                            key: 0,
                                            src: item.raw.logoUrl,
                                            alt: item.raw.name,
                                            class: "merchant-logo",
                                            onError: handleImageError
                                        }, null, 40, _hoisted_6))
                                        : _createCommentVNode("", true),
                                    _createElementVNode("span", null, _toDisplayString(item.raw.name), 1)
                                ])
                            ]),
                            _: 2
                        }, 1032, ["modelValue", "onUpdate:modelValue", "items"])
                    ]),
                    "add-cell-action": _withCtx(({ addRowData }) => [
                        _createVNode(_component_v_select, {
                            modelValue: addRowData.action,
                            "onUpdate:modelValue": ($event) => ((addRowData.action) = $event),
                            items: actionOptions,
                            "item-title": "label",
                            "item-value": "value",
                            density: "compact",
                            variant: "outlined",
                            "hide-details": ""
                        }, null, 8, ["modelValue", "onUpdate:modelValue"])
                    ]),
                    "add-cell-newDiscountRate": _withCtx(({ addRowData }) => [
                        (addRowData.action !== 'rate')
                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, "🔒 Not needed"))
                            : (_openBlock(), _createBlock(_component_v_text_field, {
                                key: 1,
                                modelValue: addRowData.newDiscountRate,
                                "onUpdate:modelValue": ($event) => ((addRowData.newDiscountRate) = $event),
                                type: "number",
                                step: "0.01",
                                min: "0",
                                suffix: "%",
                                density: "compact",
                                variant: "outlined",
                                "hide-details": ""
                            }, null, 8, ["modelValue", "onUpdate:modelValue"]))
                    ]),
                    "add-cell-scheduleDateTime": _withCtx(({ addRowData }) => [
                        _createVNode(_component_v_text_field, {
                            modelValue: addRowData.scheduleDateTime,
                            "onUpdate:modelValue": ($event) => ((addRowData.scheduleDateTime) = $event),
                            type: "datetime-local",
                            min: _unref(nowLocal),
                            density: "compact",
                            variant: "outlined",
                            "hide-details": ""
                        }, null, 8, ["modelValue", "onUpdate:modelValue", "min"])
                    ]),
                    _: 1
                }, 8, ["data", "loading", "adding-row", "deleting-row-id"]),
                _createVNode(_component_v_snackbar, {
                    modelValue: showErrorToast.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((showErrorToast).value = $event)),
                    timeout: 4000,
                    location: "bottom",
                    color: "error"
                }, {
                    actions: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                            icon: "mdi-close",
                            size: "small",
                            onClick: _cache[0] || (_cache[0] = ($event) => (showErrorToast.value = false))
                        })
                    ]),
                    default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(errorMessage.value) + " ", 1)
                    ]),
                    _: 1
                }, 8, ["modelValue"])
            ]));
        };
    }
});
